import { Dictionary } from "@ngrx/entity"
import { createSelector } from "@ngrx/store"
import { environment } from "../../../environments/environment"
import { BudgetSectionGridData, PaymentGridData } from "../../helpers/grid-helpers"
import { capitalize } from "../../helpers/helpers"
import { flatten, jsonCopyObject, listsShareSomeElement, makeMapFromListOfObjectsAndKey, sortByProp } from "globals/helpers/helpers"
import { QuoteTableData } from "../../helpers/table-column-types"
import { allDefaultQuotesColumns, quoteColumnsById, tableLightColors } from "../../helpers/table-helpers"
import { makePrettyNumber } from "../../services/pipes/prettyNumbers.pipe"
import { FilterGroup } from "globals/store/filters.data"
import { JoiResource } from "globals/store/resources.data"
import { columnOrderTypes, settingsStateSelector } from "../settings.era"
import { shareUrlsByType } from "globals/store/share.data"
import { WFWorker } from "globals/store/workforce.data"
import { Budget, BudgetQuoteSection, PublicQuoteInfo } from "./budget.data"
import { getGridDataFromSection, scheduleToScheduleGridData } from "./budget.reselector.helpers"
import { ManageQuoteData } from "./quotes.data"
import { accountBudgetsReselector } from "./budget.reselector"
import { JoiEvent } from 'globals/store/events.data';
import { resourceListReselector } from "./entity.decircular.reselector"
import { calculateLineItemBaseAmount, getConversionRate, updateBudgetSectionInPlace } from "globals/helpers/budget.helper"
import { budgetStateSelector } from "../budget.era"
import { resourcesStateSelector } from "../resources.era"
import { workforceStateSelector } from "../workforce.era"
import { filterStateSelector } from "../filters.era"
import { shareStateSelector } from "../share.era"
import { eventStateSelector } from "../events.era"

const budget = createSelector(budgetStateSelector, (state) => state.budget)
const resources = createSelector(resourcesStateSelector, (state) => state.entities)
const workersByEmail = createSelector(workforceStateSelector, (state) => state.workersByEmail)
const quoteTableFilters = createSelector(filterStateSelector, (state) => state.quotes)
const accountQuoteTableFilters = createSelector(filterStateSelector, (state) => state.accountQuotes)
const quoteShareables = createSelector(shareStateSelector, (state) => state.quote)
const userSettings = createSelector(settingsStateSelector, (state) => state.user)
const events = createSelector(eventStateSelector, (state) => state.entities)
const event = createSelector(eventStateSelector, (state) => state.event)

const SearchableResourceFields = ['name', 'resource']

export const quoteSectionReselector = createSelector(
  budget,
  resources,
(budget: Budget, resources: Dictionary<JoiResource>, { resourceId, quoteId }: ManageQuoteData): { gridData:  BudgetSectionGridData[], scheduleGridData: PaymentGridData[], quote: BudgetQuoteSection }  => {
    const resource = jsonCopyObject(resources[resourceId])
    const quote = !resource ? null : jsonCopyObject(budget.quotes[quoteId])
    try {
      updateBudgetSectionInPlace(quote, budget.currency)
    } catch (error) {
      console.error('Failed to update budget section in place:', error)
    }

    return {
      gridData: !resource || !quote ? [] : getGridDataFromSection(quote, budget.currency),
      scheduleGridData: !resource || !quote ? [] : scheduleToScheduleGridData(quote.schedule, quote.total),
      quote
    }
  }
)

export const linkableQuotesReselector = createSelector(
  budget,
  resourceListReselector,
  (budget: Budget, resourceList: JoiResource[]): BudgetQuoteSection[] => {
    if (!budget || !resourceList?.length) return []
    const allQuoteLinkIds = budget.costIds.map(id => budget.sections[id]).filter(s => s.isQuote).map(s => s.linkId)
    const linkableQuotes = flatten(resourceList.map(r => {
      const quotes = r.quoteIds.map(qId => budget.quotes[qId]).filter(q => q && !allQuoteLinkIds.includes(q.id))
      quotes.forEach(q => q.dialogName = `${r.name || 'Unnamed Resource'} ${q.name || 'Unnamed Quote'} ${makePrettyNumber(q.total || 0)}`)
      return quotes
    })).sort(sortByProp('dialogName'))
    return linkableQuotes
  }
)

export const makeQuoteTableRow = (quote: BudgetQuoteSection, resource: JoiResource, contact: WFWorker, info: PublicQuoteInfo, event: JoiEvent, budgetCurrency: string) => {
  
  const status = info?.status || null
  const quoteStatus = info?.quoteStatus || null
  const { submissionDeadline, lastSubmitted } = quote
  
  const total = quote.total || 0
  const discount = quote.discount || 0
  const conversionRate = getConversionRate(quote, budgetCurrency)
  const preEverythingTotal = quote.lineItems
                              .map(li => calculateLineItemBaseAmount(li, conversionRate))
                              .reduce((t, a) => t + a, 0) 
  const discountPercent = discount ? (100 * discount / preEverythingTotal) : 0

  const tableData: QuoteTableData = {
    id: quote.id,
    quoteId: quote.id,
    resourceId: resource.id,
    eventName: { text: event.title || '' },
    resource: { text: resource.name },
    name: { text: quote.name },
    link: { url: quote.quoteLink },
    notes: { text: quote.notes },
    type: { text: quote.quoteType },
    quoteStatus: !quoteStatus ? null : {
      percent: quoteStatus === 'Accepted' ? 1 : quoteStatus === 'Pending' ? 0 : -1,
      lights: [{
        text: quoteStatus,
        color: quoteStatus === 'Declined' ? tableLightColors.red : quoteStatus === 'Accepted' ? tableLightColors.green : tableLightColors.yellow,
      }]},
    status: !status ? null : {
      percent: status === 'Submitted' ? 1 : status === 'Active' ? 0 : -1,
      lights: [{
        text: capitalize(status),
        color: status === 'Off' ? tableLightColors.gray : status === 'Submitted' ? tableLightColors.green : tableLightColors.yellow,
      }]},
    url: { url: status !== 'Off' ? environment.joi.shareBase + shareUrlsByType.quote + '/' + quote.id : null },
    deadline: { date: !submissionDeadline ? null : new Date(submissionDeadline), HHMMText: '' },
    submitted: { date: !lastSubmitted ? null : new Date(lastSubmitted), HHMMText: '' },
    total: { text: makePrettyNumber(total) },
    discountAmount: { text: makePrettyNumber(discount) },
    discountPercent: { text: makePrettyNumber(discountPercent) },
    workforceContact: !contact ? null : contact,
  }
  return tableData
}


export const accountQuotesTableReselector = createSelector(
  accountBudgetsReselector,
  events,
  quoteShareables,
  accountQuoteTableFilters,
  (budgets, events, quoteShareables, filters) => {
    const quoteTableData: QuoteTableData[] = []
    const searchTerm = filters?.search ? filters.search.toLocaleLowerCase() : null

    budgets.forEach(budget => {
      const event = events[budget.eventId]
      if (!event) return

      console.log({budget})
      console.log({events})
      Object.values(budget.quotes).forEach(quote => {
        const resource = event.resources[quote.resourceId]
        if (!resource) return
        const contact = event.workforce.find(wf => wf.email === resource.workforceContactEmail)
        try {
          const row = makeQuoteTableRow(quote, resource, contact, quoteShareables[quote.id], event, budget.currency)
          const passes = !searchTerm || [row.eventName.text, row.type.text, row.resource.text].some(text => text && text.toLocaleLowerCase().includes(searchTerm))
          if (passes) quoteTableData.push(row)
        } catch (error) {
          console.error('Error processing quote:', quote, error)
        }
      })
    })
  try {
    const quoteTableRowsById = makeMapFromListOfObjectsAndKey(quoteTableData, 'id')
    return { quoteTableData, quoteTableRowsById }
  } catch (error) {
    console.error('Error processing quote table data:', error)
    return { quoteTableData: [], quoteTableRowsById: {} }
  }
})

export const quotesTableReselector = createSelector(
  budget,
  resourceListReselector,
  workersByEmail,
  quoteTableFilters,
  quoteShareables,
  event,
  ( 
    budget: Budget,
    resourceList: JoiResource[], 
    workersByEmail: { [email:string]: WFWorker }, 
    filters: FilterGroup,
    quoteShareables: Dictionary<PublicQuoteInfo>,
    event
  ) => {
    if (!budget || !event) return { quoteTableData: [], quoteTableRowsById: {} }
    const { search, resourceLabels, tags } = filters
    const searchTerm = (search || '').toLocaleLowerCase()
    const filterResource = (r: JoiResource): boolean => [
      !searchTerm || SearchableResourceFields.some(field => r[field] && r[field].toLocaleLowerCase().includes(searchTerm)),
      !resourceLabels || listsShareSomeElement(resourceLabels, r.labels),
      !tags || tags.includes(r.workforceContactEmail)
    ].every(passes => passes)
    
    const quoteTableData: QuoteTableData[] = flatten(resourceList.filter(filterResource).map(resource => resource.quoteIds
      .map(qId => budget.quotes[qId])
      .filter(q => q)
      .map(quote => makeQuoteTableRow(quote, resource, workersByEmail[resource.workforceContactEmail], quoteShareables[quote.id], event, budget.currency)
    )))
      
    const quoteTableRowsById = makeMapFromListOfObjectsAndKey(quoteTableData, 'id')
    return { quoteTableData, quoteTableRowsById }
  }
)

export const quoteColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const selectedColumns = !settings ? null : settings[`${columnOrderTypes.quotes}_columns`]
    // console.log({ selectedColumns, settings })
    return (!selectedColumns ? allDefaultQuotesColumns : selectedColumns.filter(c => c.selected).map(c => quoteColumnsById[c.value]))
  }
)